/**
 * AdminRevenue (/admin/analytics/revenue) — live P&L snapshot for super-admin.
 *
 * Auto-refreshes every 30 seconds so new signups + activate-annual actions
 * flow into the view without a manual reload. All figures come from
 * GET /api/admin/analytics/revenue which is the single source of truth.
 *
 * UI Architecture Parity (2026-09-05 directive): renders inside the shared
 * Sidebar/AppLayout shell (no own full-page wrapper) and uses
 * DashboardGridWrapper for drag-and-drop/resize parity with Admin.jsx home
 * and AdminSymbols.jsx.
 */
import { useEffect, useState } from "react";
import { TrendingUp, Wallet, Landmark, Percent, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { fmtKES } from "@/lib/api";
import { Jargon } from "@/components/admin/JargonTerm";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";

export default function AdminRevenue() {
    const [data, setData]     = useState(null);
    const [refreshing, setR]  = useState(false);
    const [err, setErr]       = useState(null);
    const [tick, setTick]     = useState(0);

    useEffect(() => {
        let alive = true;
        (async () => {
            setR(true);
            try {
                const r = await api.get("/admin/analytics/revenue");
                if (alive) { setData(r.data); setErr(null); }
            } catch (e) {
                if (alive) setErr(e?.response?.data?.detail || "Failed to load");
            } finally {
                if (alive) setR(false);
            }
        })();
        return () => { alive = false; };
    }, [tick]);

    // Auto-refresh every 30 seconds so new activations flow in live.
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 30_000);
        return () => clearInterval(id);
    }, []);

    if (!data && !err) {
        return <div className="p-8 text-ui-base text-muted-foreground">Loading revenue analytics…</div>;
    }
    if (err) {
        return <div className="p-8 text-ui-base text-danger">{err}</div>;
    }

    const brokeEven = data.breakeven?.already_broke_even;
    const netKes = data.net_ytd_kes;

    const revenueWidgets = [
        {
            id: "revenue-kpis", title: "Hero KPIs", category: "Overview",
            layout: { x: 0, y: 0, w: 12, h: 3 }, minW: 6, minH: 2, noPadding: true,
            render: () => (
                <section className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3" data-testid="revenue-kpis">
                    <KpiTile
                        icon={<Wallet />}
                        label="Locked-in revenue"
                        value={fmtKES(data.locked_in_kes.total)}
                        sub={`${data.totals.annual_all} annual · CMA + MoE combined`}
                        tone="ok" testId="kpi-locked-in" />
                    <KpiTile
                        icon={<TrendingUp />}
                        label={<Jargon term="run_rate" />}
                        value={fmtKES(data.run_rate_monthly)}
                        sub="Locked-in ÷ 12"
                        tone="ok" testId="kpi-run-rate" />
                    <KpiTile
                        icon={<Percent />}
                        label={<>Net <Jargon term="ytd" /></>}
                        value={fmtKES(netKes)}
                        sub={`After ${fmtKES(data.overhead.ytd)} overhead (${data.overhead.months_elapsed} mo)`}
                        tone={netKes >= 0 ? "ok" : "danger"} testId="kpi-net-ytd" />
                    <KpiTile
                        icon={<Landmark />}
                        label="Trial pipeline"
                        value={fmtKES(data.trial_pipeline_kes.total)}
                        sub={`${data.totals.trial_all} agencies mid-trial`}
                        tone="warn" testId="kpi-trial-pipeline" />
                </section>
            ),
        },
        {
            id: "revenue-breakeven", title: "Breakeven Runway", category: "Overview",
            layout: { x: 0, y: 3, w: 12, h: 4 }, minW: 6, minH: 3,
            render: () => (
                <div className="space-y-2" data-testid="revenue-breakeven">
                    <div className="flex flex-wrap justify-between items-center gap-2">
                        <div>
                            <div className="overline"><Jargon term="breakeven_runway" /></div>
                            <div className="text-ui-base">
                                Locked-in covers{" "}
                                <b>{data.breakeven.months_covered} months</b>{" "}
                                of {fmtKES(data.overhead.monthly)}/mo overhead
                                {brokeEven ? (
                                    <span className="ml-2 text-emerald-500 font-bold">· ✓ Already broke even YTD</span>
                                ) : (
                                    <span className="ml-2 text-amber-500 font-bold">
                                        · Need {Math.max(1, data.overhead.months_elapsed - Math.floor(data.breakeven.months_covered))} more months of coverage
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-caption text-muted-foreground font-mono">
                            {fmtKES(data.overhead.annual)}/year overhead target
                        </div>
                    </div>
                    <BreakevenBar covered={data.breakeven.months_covered} total={12} />
                </div>
            ),
        },
        {
            id: "revenue-tiers", title: "Revenue by Tier", category: "Breakdown",
            layout: { x: 0, y: 7, w: 12, h: 9 }, minW: 6, minH: 6,
            render: () => (
                <section className="grid md:grid-cols-2 gap-4">
                    <TierRevenueCard
                        title="CMA institutions"
                        tag="cma"
                        annualPrice={data.pricing.cma_annual_kes}
                        counts={data.counts.cma}
                        lockedIn={data.locked_in_kes.cma}
                        trialPipeline={data.trial_pipeline_kes.cma}
                    />
                    <TierRevenueCard
                        title="MoE institutions"
                        tag="moe"
                        annualPrice={data.pricing.moe_annual_kes}
                        counts={data.counts.moe}
                        lockedIn={data.locked_in_kes.moe}
                        trialPipeline={data.trial_pipeline_kes.moe}
                    />
                </section>
            ),
        },
        {
            id: "revenue-projection", title: "End-of-Year Projection", category: "Forecast",
            layout: { x: 0, y: 16, w: 12, h: 6 }, minW: 6, minH: 4,
            render: () => (
                <div data-testid="revenue-projection">
                    <div className="overline text-primary mb-2">End-of-year projection</div>
                    <div className="grid md:grid-cols-4 gap-3 text-ui-base">
                        <MiniStat
                            label="Activations (last 90d)"
                            value={data.projection.activations_last_90d}
                            sub={`${data.projection.monthly_activation_rate}/month rate`} />
                        <MiniStat
                            label="Projected new signups"
                            value={data.projection.projected_new_signups.toFixed(1)}
                            sub={`Through Dec (${data.projection.remaining_months_this_year} months left)`} />
                        <MiniStat
                            label="Projected EOY revenue"
                            value={fmtKES(data.projection.projected_eoy_locked_in_kes)}
                            sub={fmtKES(data.projection.projected_new_revenue_kes) + " new"}
                            tone="primary" />
                        <MiniStat
                            label="Projected EOY net"
                            value={fmtKES(data.projection.projected_eoy_net_kes)}
                            sub={`After ${fmtKES(data.overhead.annual)} annual overhead`}
                            tone={data.projection.projected_eoy_net_kes >= 0 ? "success" : "danger"} />
                    </div>
                    <p className="text-caption text-muted-foreground mt-3">
                        Linear extrapolation from the last-90-day activation velocity. Assumes today's CMA/MoE mix holds.
                        Actual outcomes will vary as you scale marketing and referrals.
                    </p>
                </div>
            ),
        },
        {
            id: "revenue-recent", title: "Recent Annual Activations", category: "Activity",
            layout: { x: 0, y: 22, w: 12, h: 7 }, minW: 6, minH: 4,
            render: () => (
                <div data-testid="revenue-recent-activations">
                    <div className="overline mb-3">Recent annual activations</div>
                    {data.recent_activations.length === 0 ? (
                        <div className="text-ui-base text-muted-foreground italic">
                            No annual activations yet. The moment super-admin flips a trial to annual,
                            it'll appear here.
                        </div>
                    ) : (
                        <ul className="space-y-2 text-ui-base">
                            {data.recent_activations.map((r, i) => (
                                <li key={r.agency_id + i}
                                    className="flex justify-between items-center py-1 border-b border-border last:border-0"
                                    data-testid={`recent-activation-${i}`}>
                                    <div className="min-w-0">
                                        <span className="font-bold">{r.agency_name}</span>
                                        <span className="ml-2 text-caption font-bold uppercase tracking-widest text-muted-foreground">
                                            {r.institution_type}
                                        </span>
                                        <div className="text-caption text-muted-foreground font-mono">
                                            {new Date(r.ts).toLocaleString()} · ref {r.payment_reference || "—"} · by {r.verified_by || "—"}
                                        </div>
                                    </div>
                                    <div className="font-mono font-bold text-emerald-500">
                                        +{fmtKES(r.amount_kes)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6" data-testid="admin-revenue-page">
            <PageHeader
                icon={TrendingUp}
                eyebrow="Modules"
                title="Revenue Analytics"
                subtitle={
                    <>Live <Jargon term="pnl" /> snapshot · auto-refresh every 30s · generated {new Date(data.generated_at).toLocaleString()}</>
                }
                actions={
                    <button
                        type="button"
                        onClick={() => setTick((t) => t + 1)}
                        disabled={refreshing}
                        data-testid="revenue-refresh"
                        className="text-ui-base font-bold px-3 py-1.5 rounded border border-border hover:border-primary inline-flex items-center gap-1"
                    >
                        <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Refreshing…" : "Refresh now"}
                    </button>
                }
                testId="admin-revenue-header"
            />

            <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-revenue" widgets={revenueWidgets} />
        </div>
    );
}

function KpiTile({ icon, label, value, sub, tone, testId }) {
    const toneCls =
        tone === "danger" ? "text-danger"
        : tone === "warn" ? "text-amber-500"
        : tone === "ok"   ? "text-success"
        : "text-foreground";
    return (
        <div className="rounded-lg border border-border bg-card p-3" data-testid={testId}>
            <div className="flex items-center gap-2 text-muted-foreground">
                <span className="w-4 h-4 opacity-80">{icon}</span>
                <span className="text-caption uppercase tracking-widest font-bold">{label}</span>
            </div>
            <div className={`font-heading text-h3 mt-1 ${toneCls}`}>{value}</div>
            <div className="text-caption text-muted-foreground mt-1">{sub}</div>
        </div>
    );
}

function BreakevenBar({ covered, total }) {
    const pct = Math.min(100, Math.max(0, (covered / total) * 100));
    return (
        <div className="w-full h-3 rounded-full bg-muted overflow-hidden border border-border" data-testid="breakeven-bar">
            <div
                className={`h-full transition-all ${pct >= 100 ? "bg-emerald-500" : "bg-primary"}`}
                style={{ width: `${pct}%` }}
                aria-label={`${pct.toFixed(0)}% of annual overhead covered`}
            />
        </div>
    );
}

function TierRevenueCard({ title, tag, annualPrice, counts, lockedIn, trialPipeline }) {
    return (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3"
             data-testid={`tier-revenue-${tag}`}>
            <header>
                <div className="overline">{title}</div>
                <div className="text-ui-base text-muted-foreground">
                    {fmtKES(annualPrice)} / year list price
                </div>
            </header>
            <div className="grid grid-cols-5 gap-1 text-center">
                {["pending", "trial", "annual", "expired", "monthly"].map((k) => (
                    <div key={k} className="rounded border border-border bg-background/60 p-1.5">
                        <div className="text-[8px] font-bold uppercase tracking-widest text-muted-foreground">
                            {k}
                        </div>
                        <div className="font-mono font-black text-ui-base">{counts[k] || 0}</div>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-ui-base">
                <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-2">
                    <div className="text-caption font-bold uppercase tracking-widest text-emerald-500">Locked in</div>
                    <div className="font-mono font-black text-emerald-500">{fmtKES(lockedIn)}</div>
                </div>
                <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2">
                    <div className="text-caption font-bold uppercase tracking-widest text-amber-500">Trial pipeline</div>
                    <div className="font-mono font-black text-amber-500">{fmtKES(trialPipeline)}</div>
                </div>
            </div>
        </div>
    );
}

function MiniStat({ label, value, sub, tone }) {
    const cls =
        tone === "success" ? "text-success"
        : tone === "danger" ? "text-danger"
        : tone === "primary" ? "text-primary"
        : "text-foreground";
    return (
        <div className="rounded border border-border bg-background/60 p-2">
            <div className="text-caption font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`font-heading text-h3 ${cls}`}>{value}</div>
            <div className="text-caption text-muted-foreground">{sub}</div>
        </div>
    );
}
